import type { MixedItemDist, MixedItemTag } from "@memorang/types";

import { isScreenshotMode } from "@constants/common";
import { getParentMetadata } from "@helpers/content/metadata";
/* -----------------Helpers & Hooks--------------- */
import { groupTagsByType } from "@helpers/content/tag";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { getMasteryPercentage } from "@memorang/helpers/common";
import { formatList, getCurrentLocale } from "@memorang/helpers/i18n";
import { t } from "i18next";
/* -----------------Child components--------------- */
import {
	buildGroupSectionTitle,
	getSectionTags,
	getStudyPackItemCount,
	withFreeAllowanceDescriptions,
	withResponseCountDescriptions,
} from "./tag";

const EMPTY_DISTRIBUTION = { high: 0, medium: 0, low: 0, none: 0 } as const;

/**
 * Premium skills arrive as bare titles with no id, count, or distribution. Build them into
 * tags the section machinery can render as locked rows.
 */
const buildPremiumSkillTags = (premiumSkills: string[]): MixedItemTag[] =>
	premiumSkills.map((title, index) => ({
		id: -(index + 1),
		title,
		type: "skill",
		displayType: ["Display"],
		locked: true,
		distribution: { ...EMPTY_DISTRIBUTION },
		items: [],
		children: [],
		numItems: 0,
		count: 0,
		score: 0,
		totalResponses: 0,
	}));

const getDashboardData = (
	response: MixedItemDist,
	handleNavigateToIAP: () => void,
	groupByCategory?: boolean | null,
	/**
	 * Purchase-or-bypassed access — deliberately NOT "is content unlocked"
	 * (hasUnlockedStudyPackContent / hasStudyPackContentAccess). A free-collection
	 * user with no purchase already resolves content-access to true, which would
	 * wrongly suppress the locked premium rows below (computeLocked would unlock every
	 * tag, and the !hasAccess guard here would skip injecting premiumSkills). This
	 * must stay purchase-only so the premium rows and free-allowance upsell still
	 * render for them.
	 */
	hasStudyPackPurchaseAccess?: boolean,
) => {
	const {
		contents = [],
		iconUrl,
		title,
		tags,
		freeTagIds,
		premiumSkills,
	} = response;
	const numItems = getStudyPackItemCount(contents);

	const metadata = getParentMetadata(contents);

	const { distribution } = metadata;

	const hasAccess = hasStudyPackPurchaseAccess ?? response.hasAccess;
	const hasPremiumOffering = !hasAccess && Boolean(premiumSkills?.length);
	const tagsByType = groupTagsByType(tags, "Display", freeTagIds, hasAccess);

	if (!hasAccess && premiumSkills?.length) {
		tagsByType.skill = [
			...(tagsByType.skill ?? []),
			...buildPremiumSkillTags(premiumSkills),
		];
	}

	const freeSkillTags = (tagsByType.skill ?? []).filter((tag) => !tag.locked);
	const freeItemCount = new Set(
		freeSkillTags.flatMap((tag) => (tag.items ?? []).map((item) => item.id)),
	).size;

	const freeSkillTagIds = freeSkillTags.map((tag) => tag.id);

	const freeContentBanner =
		hasPremiumOffering && freeItemCount > 0
			? {
					title: t("dashboard.studyPack.freeContentBannerTitle", {
						count: freeItemCount,
					}),
					body: t("dashboard.studyPack.freeContentBannerBody", {
						skills: formatList(
							getCurrentLocale(),
							freeSkillTags.map((tag) => tag.title),
						),
					}),
					ctaLabel: t("dashboard.studyPack.freeContentBannerCta"),
				}
			: undefined;

	const sectionTags = getSectionTags(
		numItems,
		tagsByType,
		tags,
		handleNavigateToIAP,
		hasAccess,
	);

	const masteryPercentage = getMasteryPercentage(distribution);

	const sections = Object.keys(tagsByType)
		.filter((type) => tagsByType[type].length > 0)
		.sort((a, b) => a.localeCompare(b))
		.map((type) => {
			const typeTags = tagsByType[type];
			return {
				title: buildGroupSectionTitle(type, typeTags, numItems),
				tags: typeTags,
			};
		});

	const finalSectionTags = withFreeAllowanceDescriptions(
		withResponseCountDescriptions(
			groupByCategory
				? sections.map((section) => ({
						...section,
						tags: section.tags.map((tag) => ({
							...tag,
							distribution: isScreenshotMode
								? randomizeDistribution(tag.distribution)
								: tag.distribution,
						})),
					}))
				: sectionTags,
		),
		{ hasPremiumOffering },
	);
	return {
		distribution: isScreenshotMode
			? randomizeDistribution(distribution)
			: distribution,
		productTitle: title,
		productImage: iconUrl,
		sectionTags: finalSectionTags,
		masteryPercentage,
		numItems,
		freeContentBanner,
		freeSkillTagIds,
	};
};

export { getDashboardData };
